import { useCallback, useEffect, useMemo, useState } from 'react'
import { ActionIcon, Button, Group, ScrollArea, Stack, Text, Tooltip } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { IconPackage } from '@/components/icons'
import { AppModal } from '@/components/ui'
import { api, ApiError } from '@/lib/api'
import type { InstallerRow } from '@/types/api'

const PLATFORM_LABELS: Record<string, string> = {
  android: 'Android',
  windows: 'Windows',
  macos: 'macOS',
  linux: 'Linux',
}

function formatInstallerSize(bytes: number): string {
  if (!bytes) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`
}

function groupByPlatform(rows: InstallerRow[]): Record<string, InstallerRow[]> {
  return rows.reduce<Record<string, InstallerRow[]>>((acc, row) => {
    const p = row.platform || 'other'
    if (!acc[p]) acc[p] = []
    acc[p].push(row)
    return acc
  }, {})
}

function platformTitle(platform: string): string {
  return PLATFORM_LABELS[platform] || platform
}

type Props = {
  onPick: (installer: InstallerRow) => void
  disabled?: boolean
  /** Уже добавленные id — кнопка «Добавить» не дублирует */
  existingIds?: Set<string>
}

/**
 * Кнопка у поля ответа: модалка со списком установщиков workspace (как classic TicketDetail).
 */
export function ReplyInstallerPickerButton({ onPick, disabled, existingIds }: Props) {
  const [opened, setOpened] = useState(false)
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState<InstallerRow[]>([])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const list = await api.installersList()
      setRows(Array.isArray(list) ? list : [])
    } catch (e) {
      setRows([])
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось загрузить установщики',
        color: 'red',
      })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (opened) void load()
  }, [opened, load])

  const grouped = useMemo(() => groupByPlatform(rows), [rows])
  const platformKeys = useMemo(() => Object.keys(grouped).sort(), [grouped])

  const handleAdd = (inst: InstallerRow) => {
    if (existingIds?.has(inst.id)) {
      showNotification({ title: 'Уже добавлено', message: inst.name, color: 'yellow' })
      return
    }
    onPick(inst)
    setOpened(false)
  }

  return (
    <>
      <Tooltip label="Прикрепить установщик" withArrow position="top">
        <ActionIcon
          type="button"
          variant="subtle"
          size="sm"
          radius="md"
          color={opened ? 'brand' : 'gray'}
          aria-label="Прикрепить установщик"
          disabled={disabled}
          style={{ pointerEvents: 'auto' }}
          onClick={() => setOpened(true)}
        >
          <IconPackage size={16} stroke={1.5} />
        </ActionIcon>
      </Tooltip>

      <AppModal
        opened={opened}
        onClose={() => setOpened(false)}
        title="Выберите установщик"
        size="md"
        scrollAreaComponent={ScrollArea.Autosize}
      >
        {loading ? (
          <Text size="sm" c="dimmed">
            Загрузка…
          </Text>
        ) : rows.length === 0 ? (
          <Text size="sm" c="dimmed">
            В workspace нет активных установщиков. Их можно добавить в разделе «Установщики».
          </Text>
        ) : (
          <Stack gap="lg">
            {platformKeys.map((pk) => (
              <Stack key={pk} gap="sm">
                <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                  {platformTitle(pk)}
                </Text>
                <Stack gap="xs">
                  {(grouped[pk] || []).map((inst) => {
                    const already = existingIds?.has(inst.id)
                    return (
                      <Group
                        key={inst.id}
                        justify="space-between"
                        align="flex-start"
                        wrap="nowrap"
                        gap="sm"
                        p="xs"
                        style={{
                          borderRadius: 8,
                          border: '1px solid var(--mantine-color-default-border)',
                        }}
                      >
                        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                          <Text size="sm" fw={600} lineClamp={2}>
                            {inst.name}
                          </Text>
                          {inst.description ? (
                            <Text size="xs" c="dimmed" lineClamp={2}>
                              {inst.description}
                            </Text>
                          ) : null}
                          <Text size="xs" c="dimmed">
                            {inst.version ? `v${inst.version} · ` : ''}
                            {formatInstallerSize(inst.file_size)}
                          </Text>
                        </Stack>
                        <Button
                          size="compact-xs"
                          disabled={already}
                          onClick={() => handleAdd(inst)}
                        >
                          {already ? 'Добавлен' : 'Добавить'}
                        </Button>
                      </Group>
                    )
                  })}
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}
      </AppModal>
    </>
  )
}
